import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import ReportsHeaderSearch from "../reports/ReportsHeaderSearch";
import { useReportsHeader } from "../../context/ReportsHeaderContext";
import { signOut } from "../../utils/adminSession";
import { openReachOutInNewTab, getReachOutBaseUrl } from "../../utils/crossAppSso";
import { adminAuthAPI } from "../../services/api";
import { isWizetokProductEnabled, readStoredEntitlements } from "../../utils/entitlements";
import PermissionDeniedPanel from "../PermissionDeniedPanel";
import TenantSelectModal, { clearTenantChoice, tenantChoiceConfirmed } from "../TenantSelectModal";
import { useRules } from "../../hooks/useRules";
import {
  canAccessWizetokNav,
  installWizetokViewerWriteGuard,
  isReadonlyAdmin,
  isWizetokWritePath,
  readStoredAdmin,
  wizetokRoleLabel,
} from "../../utils/roleAccess";
import "./AdminLayout.css";

const C = {
  brand: "#6366F1",
  purple: "#8B5CF6",
  border: "#E5E7EB",
  surfaceAlt: "#F3F4F6",
  t1: "#111827",
  t4: "#9CA3AF",
};

function getInitials(nameOrEmail) {
  const raw = (nameOrEmail || "").trim();
  if (!raw) return "AD";
  const base = raw.includes("@") ? raw.split("@")[0] : raw;
  const parts = base.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function breadcrumbTitle(pathname) {
  if (pathname.startsWith("/admin/dashboard")) return "Dashboard";
  if (pathname.startsWith("/admin/analytics")) return "Analytics";
  if (pathname.startsWith("/admin/campaigns/new")) return "New Position";
  if (pathname.includes("/edit") && pathname.startsWith("/admin/campaigns/")) return "Edit Position";
  if (/^\/admin\/campaigns\/[^/]+$/.test(pathname)) return "Position";
  if (pathname === "/admin/campaigns") return "All Positions";
  if (pathname.startsWith("/admin/question-bank")) return "Question Bank";
  if (pathname.startsWith("/admin/candidates")) return "Candidates";
  if (pathname.startsWith("/admin/interviews")) return "Interviews";
  if (pathname.startsWith("/admin/monitor")) return "Live Monitor";
  if (pathname.startsWith("/admin/reports")) return "Reports";
  if (pathname.startsWith("/admin/settings")) return "Settings";
  if (pathname.startsWith("/admin/administration")) return "Administration";
  return "Admin";
}

const NAV = [
  {
    section: "Overview",
    items: [
      {
        id: "dashboard",
        path: "/admin/dashboard",
        label: "Dashboard",
        d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
      },
      {
        id: "analytics",
        path: "/admin/analytics",
        label: "Analytics",
        d: "M18 20V10M12 20V4M6 20v-6",
      },
    ],
  },
  {
    section: "Hiring",
    items: [
      {
        id: "campaigns",
        path: "/admin/campaigns",
        label: "All Positions",
        d: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z",
      },
      {
        id: "question-bank",
        path: "/admin/question-bank",
        label: "Question Bank",
        d: "M12 6.253v13M12 6.253C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
      },
      {
        id: "candidates",
        path: "/admin/candidates",
        label: "Candidates",
        d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2",
      },
      {
        id: "interviews",
        path: "/admin/interviews",
        label: "Interviews",
        d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
      },
      {
        id: "monitor",
        path: "/admin/monitor",
        label: "Live Monitor",
        d: "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z",
      },
      {
        id: "reports",
        path: "/admin/reports",
        label: "Reports",
        d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
      },
    ],
  },
  {
    section: "System",
    items: [
      {
        id: "open-reachout",
        label: "Open ReachOut",
        external: "reachout",
        d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3",
      },
      {
        id: "administration",
        path: "/admin/administration",
        label: "Administration",
        d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
      },
      {
        id: "settings",
        path: "/admin/settings",
        label: "Settings",
        d: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
      },
    ],
  },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [entitlements, setEntitlements] = useState(readStoredEntitlements);
  const [entitlementsReady, setEntitlementsReady] = useState(() => readStoredEntitlements() != null);
  const [reachoutDenied, setReachoutDenied] = useState(false);
  const [admin, setAdmin] = useState(readStoredAdmin);
  const pageTitle = breadcrumbTitle(location.pathname);
  const reportsHeader = useReportsHeader();
  const showReportsSearch =
    location.pathname.startsWith("/admin/reports") && reportsHeader?.api?.sessions;
  const displayName = admin?.full_name || admin?.name || admin?.email?.split("@")[0] || "Admin";
  const assignedRole = wizetokRoleLabel(admin);
  const initials = getInitials(admin?.full_name || admin?.name || admin?.email || "Admin");

  const tenants = Array.isArray(admin?.available_tenants) ? admin.available_tenants : [];
  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  // Several tenants and no choice made in this tab yet → ask before showing any data.
  const mustPickTenant = entitlementsReady && tenants.length > 1 && !tenantChoiceConfirmed(admin?.tenant_id);

  const handleLogout = async () => {
    clearTenantChoice();
    await signOut();
  };

  const handleOpenReachOut = () => {
    if (entitlements?.reachout && entitlements.reachout.enabled === false) {
      setReachoutDenied(true);
      return;
    }
    setReachoutDenied(false);
    openReachOutInNewTab();
  };

  const readOnly = isReadonlyAdmin(admin);
  const rules = useRules();

  useEffect(() => {
    if (!readOnly) return undefined;
    return installWizetokViewerWriteGuard();
  }, [readOnly]);

  useEffect(() => {
    let cancelled = false;
    adminAuthAPI.getCurrentAdmin()
      .then((res) => {
        if (cancelled) return;
          const next = res.data || {};
          try {
            const prev = JSON.parse(localStorage.getItem("admin") || "{}");
            localStorage.setItem("admin", JSON.stringify({ ...prev, ...next }));
          } catch {
            // ignore
          }
          setAdmin((prev) => ({ ...(prev || {}), ...next }));
          setEntitlements(next.entitlements || {});
        setEntitlementsReady(true);
      })
      .catch(() => {
        if (!cancelled) setEntitlementsReady(true);
      });
    return () => { cancelled = true; };
  }, []);

  if (!localStorage.getItem('token')) {
    return <Navigate to="/admin/login" replace />;
  }

  if (readOnly && isWizetokWritePath(location.pathname)) {
    return <Navigate to="/admin/campaigns" replace />;
  }

  return (
    <div className="al-shell" data-role-readonly={readOnly ? "true" : undefined}>
      <div className="al-body">
        <aside className="al-sidebar">
          <div style={{ padding: "18px 16px 14px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  background: C.brand,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 2px 8px ${C.brand}40`,
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" width="15" height="15">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.t1, letterSpacing: "-.2px" }}>Wizetok</div>
                <div style={{ fontSize: 10, color: C.t4, marginTop: 1 }}>Interview Platform</div>
              </div>
            </div>
          </div>
          <nav className="al-nav">
            {NAV.map((sec) => {
              const items = sec.items.filter((item) => canAccessWizetokNav(item.id, admin, rules));
              if (items.length === 0) return null;
              return (
              <div key={sec.section} className="al-nav-section">
                <div className="al-nav-section-label">{sec.section}</div>
                {items
                  .filter((item) => item.external !== "reachout" || Boolean(getReachOutBaseUrl()))
                  .map((item) => {
                  const active = item.path ? location.pathname.startsWith(item.path) : false;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        if (item.external === "reachout") {
                          handleOpenReachOut();
                          return;
                        }
                        setReachoutDenied(false);
                        navigate(item.path);
                      }}
                      className={`al-nav-item ${active ? "active" : ""}`}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        width="15"
                        height="15"
                        style={{ flexShrink: 0 }}
                        className="al-nav-svg"
                      >
                        {item.id === "candidates" ? (
                          <>
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
                          </>
                        ) : (
                          <path d={item.d} />
                        )}
                      </svg>
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
              );
            })}
          </nav>
          <div style={{ padding: "10px 12px 12px", borderTop: `1px solid ${C.border}` }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: "8px",
                borderRadius: 8,
                background: C.surfaceAlt,
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg,${C.brand},${C.purple})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  fontWeight: 700,
                  color: "white",
                  flexShrink: 0,
                }}
                aria-hidden
              >
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>
                  {displayName}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: C.t4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {admin?.email || ""}
                </div>
                {assignedRole ? (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#6B7280",
                      marginTop: 1,
                    }}
                  >
                    {assignedRole}
                  </div>
                ) : null}
                {admin?.tenant_name ? (
                  <div style={{ fontSize: 10, color: C.t4, marginTop: 1, display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={admin.tenant_name}>
                      {admin.tenant_name}
                    </span>
                    {tenants.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setTenantPickerOpen(true)}
                        style={{ border: "none", background: "none", padding: 0, color: C.brand, fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                      >
                        Switch
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                className="al-logout-btn"
                title="Logout"
                aria-label="Logout"
                onClick={handleLogout}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            </div>
          </div>
        </aside>
        <TenantSelectModal
          open={mustPickTenant || tenantPickerOpen}
          forced={mustPickTenant}
          tenants={tenants}
          currentId={admin?.tenant_id}
          onClose={() => setTenantPickerOpen(false)}
        />
        <main className="al-main">
          <header className="al-content-header">
            <div style={{ fontSize: 13, color: C.t4, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: C.brand, fontWeight: 600 }}>Wizetok</span>
              <span style={{ color: C.t4 }}>›</span>
              <span style={{ color: "#64748B", fontWeight: 500 }}>{pageTitle}</span>
            </div>
            <div style={{ flex: 1 }} />
            {showReportsSearch ? <ReportsHeaderSearch /> : null}
            <div className="al-live-badge">
              <span className="al-live-dot" />
              Systems operational
            </div>
            <button type="button" className="al-icon-btn" title="Notifications">
              🔔
              <span className="al-notif-dot" />
            </button>
          </header>
          <div className="al-main-scroll">
            {reachoutDenied || (entitlementsReady && !isWizetokProductEnabled(entitlements)) ? (
              <PermissionDeniedPanel productName="Wizetok" />
            ) : (
              children
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
